import { ChevronDown, Fuel, ListOrdered, Plus } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { mensagemDeErro } from '@/lib/erros';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { DropdownMenu } from '@/ui/primitives/dropdown-menu';
import { Skeleton } from '@/ui/primitives/skeleton';
import {
  type EmpresaDetalhe,
  MODELOS_REGRA_CLASSIFICACAO,
  type RegraClassificacao,
} from '@fiscoai/contracts';

import { entradaDaRegra, useRegras, useSalvarRegra } from './api';
import { DialogoApagarRegra } from './dialogo-apagar-regra';
import { EditorRegra } from './editor-regra';
import {
  type DestinoRegra,
  type Rascunho,
  rascunhoComDestino,
  rascunhoDaRegra,
  rascunhoDoModelo,
  rascunhoNovo,
} from './formulario-regra';
import { LinhaRegra } from './linha-regra';
import { itens, ROTULO_MODELO } from './textos';
import { useReordenar } from './use-reordenar';

interface Props {
  empresa: EmpresaDetalhe;
  podeEditar: boolean;
}

const [MODELO_COMBUSTIVEIS] = MODELOS_REGRA_CLASSIFICACAO;

function Cabecalho({
  podeEditar,
  aoAbrir,
}: {
  podeEditar: boolean;
  aoAbrir: (r: Rascunho) => void;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="grid gap-1">
        <h2 id="titulo-regras" className="text-lg font-semibold">
          Regras de classificação
        </h2>
        <p className="max-w-prose text-sm text-texto-suave">
          Quando um item chega sem classificação, a primeira regra ligada que casar escolhe o
          destino. A escolha feita à mão sempre vale mais.
        </p>
      </div>
      {podeEditar && (
        <div className="flex flex-wrap gap-2">
          <DropdownMenu
            gatilho={
              <Button tamanho="sm">
                Usar um modelo <ChevronDown aria-hidden />
              </Button>
            }
            itens={MODELOS_REGRA_CLASSIFICACAO.map((m) => ({
              rotulo: ROTULO_MODELO[m.id],
              aoSelecionar: () => {
                aoAbrir(rascunhoDoModelo(m));
              },
            }))}
          />
          <Button
            variante="primario"
            tamanho="sm"
            onClick={() => {
              aoAbrir(rascunhoNovo());
            }}
          >
            <Plus aria-hidden /> Nova regra
          </Button>
        </div>
      )}
    </header>
  );
}

function Vazio({ podeEditar, aoAbrir }: { podeEditar: boolean; aoAbrir: (r: Rascunho) => void }) {
  return (
    <EstadoVazio
      icone={<ListOrdered />}
      titulo="Nenhuma regra ainda"
      texto="Toda nota chega para você classificar item a item. Uma regra faz isso sozinha para o que se repete, como combustível."
      className="py-10"
      acao={
        podeEditar && (
          <div className="flex flex-wrap justify-center gap-2">
            <Button
              variante="primario"
              onClick={() => {
                aoAbrir(rascunhoDoModelo(MODELO_COMBUSTIVEIS));
              }}
            >
              <Fuel aria-hidden /> Começar pelo modelo de combustíveis
            </Button>
            <Button
              onClick={() => {
                aoAbrir(rascunhoNovo());
              }}
            >
              Criar uma regra
            </Button>
          </div>
        )
      }
    />
  );
}

/** Linha que virou editor: mantém o número (a posição não muda ao editar). */
function LinhaEditor({ numero, children }: { numero: number; children: ReactNode }) {
  return (
    <li className="grid grid-cols-[auto_1fr] gap-x-3 bg-superficie-hover px-4 py-4 animate-surgir">
      <span className="w-6 pt-7 text-right text-lg font-semibold leading-tight tabular text-marca">
        {numero}
      </span>
      {children}
    </li>
  );
}

/** Liga/desliga direto da lista, com o aviso do que acontece com os itens. */
function useAlternar(empresaId: string) {
  const salvar = useSalvarRegra(empresaId);
  const alternar = (regra: RegraClassificacao, ligar: boolean) => {
    salvar.mutate(
      { ...entradaDaRegra(regra), ativa: ligar, id: regra.id },
      {
        onSuccess: () => {
          toast.success(
            ligar || regra.itens === 0
              ? `Regra ${ligar ? 'ligada' : 'desligada'}`
              : `Regra desligada. ${itens(regra.itens)} ${regra.itens === 1 ? 'volta' : 'voltam'} para "a classificar".`,
          );
        },
        onError: (erro) => toast.error(mensagemDeErro(erro)),
      },
    );
  };
  return { alternar, alternando: salvar.isPending ? salvar.variables.id : null };
}

interface PropsLista {
  empresaId: string;
  regras: RegraClassificacao[];
  rascunho: Rascunho | null;
  podeEditar: boolean;
  aoAbrir: (r: Rascunho | null) => void;
  aoApagar: (r: RegraClassificacao) => void;
}

/** A lista numerada; a regra em edição (ou a nova, no fim) vira o editor no lugar. */
function ListaRegras({ empresaId, regras, rascunho, podeEditar, aoAbrir, aoApagar }: PropsLista) {
  const { alternar, alternando } = useAlternar(empresaId);
  const reordenar = useReordenar(empresaId, regras);
  const editor = (numero: number, r: Rascunho) => (
    <LinhaEditor key={r.regraId ?? 'nova'} numero={numero}>
      <EditorRegra
        empresaId={empresaId}
        rascunho={r}
        aoFechar={() => {
          aoAbrir(null);
        }}
      />
    </LinhaEditor>
  );
  return (
    <ol
      aria-label="Regras de classificação"
      className="divide-y divide-borda rounded-cartao border border-borda"
    >
      {regras.map((regra, i) =>
        rascunho?.regraId === regra.id ? (
          editor(i + 1, rascunho)
        ) : (
          <LinhaRegra
            key={regra.id}
            ref={reordenar.registrar(regra.id)}
            regra={regra}
            posicao={i}
            total={regras.length}
            podeEditar={podeEditar}
            ocupada={alternando === regra.id}
            arraste={reordenar.arraste(regra.id)}
            aoAlternar={(ligar) => {
              alternar(regra, ligar);
            }}
            aoMover={(d) => {
              reordenar.mover(regra.id, d);
            }}
            aoEditar={() => {
              aoAbrir(rascunhoDaRegra(regra));
            }}
            aoApagar={() => {
              aoApagar(regra);
            }}
          />
        ),
      )}
      {rascunho?.regraId === null && editor(regras.length + 1, rascunho)}
    </ol>
  );
}

/**
 * Vindo de "Usar como destino de uma regra" (aba Classificações), abre a regra nova já com o
 * destino; o estado da navegação é consumido para não reabrir ao recarregar.
 */
function useRascunhoInicial(podeEditar: boolean): Rascunho | null {
  const local = useLocation();
  const navegar = useNavigate();
  const estado: unknown = local.state;
  const destino =
    podeEditar && typeof estado === 'object' && estado !== null && 'destinoRegra' in estado
      ? (estado.destinoRegra as DestinoRegra)
      : null;
  const [inicial] = useState(() => (destino ? rascunhoComDestino(destino) : null));
  useEffect(() => {
    if (destino) void navegar(`${local.pathname}${local.search}`, { replace: true, state: null });
  }, [destino, navegar, local.pathname, local.search]);
  return inicial;
}

/** O que a aba mostra: a lista também aparece vazia quando há uma regra nova em edição. */
function vistaDaAba(
  status: 'pending' | 'error' | 'success',
  total: number,
  criando: boolean,
): 'carregando' | 'erro' | 'vazio' | 'lista' {
  if (status === 'pending') return 'carregando';
  if (status === 'error') return 'erro';
  return total === 0 && !criando ? 'vazio' : 'lista';
}

/**
 * Regras de classificação da empresa (spec 0007): lista numerada (é a prioridade), cada regra
 * escrita como frase, editor no lugar da linha, prévia ao vivo e modelos prontos.
 */
export function AbaRegras({ empresa, podeEditar }: Props) {
  const consulta = useRegras(empresa.id);
  const regras = consulta.data?.items ?? [];
  const inicial = useRascunhoInicial(podeEditar);
  const [rascunho, setRascunho] = useState<Rascunho | null>(inicial);
  const [apagando, setApagando] = useState<RegraClassificacao | null>(null);
  const vista = vistaDaAba(consulta.status, regras.length, rascunho?.regraId === null);
  return (
    <section className="grid gap-4 py-6" aria-labelledby="titulo-regras">
      <Cabecalho podeEditar={podeEditar} aoAbrir={setRascunho} />
      {vista === 'carregando' && <Skeleton className="h-32 rounded-cartao" />}
      {vista === 'erro' && <p className="text-sm text-perigo">{mensagemDeErro(consulta.error)}</p>}
      {vista === 'vazio' && <Vazio podeEditar={podeEditar} aoAbrir={setRascunho} />}
      {vista === 'lista' && (
        <ListaRegras
          empresaId={empresa.id}
          regras={regras}
          rascunho={rascunho}
          podeEditar={podeEditar}
          aoAbrir={setRascunho}
          aoApagar={setApagando}
        />
      )}
      <DialogoApagarRegra
        empresaId={empresa.id}
        regra={apagando}
        aoFechar={() => {
          setApagando(null);
        }}
      />
    </section>
  );
}
